import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal.js';
import { Dd } from '../components/Dropdown.js';
import { api } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';

/**
 * Touch Rules config (Settings mega-menu → Touch Rules). A rule is a named
 * cadence template for a contact category: an ordered set of steps, each firing
 * on `offsetDays` after the anchor across one or more channels. CRUD → /touch-rules.
 * Admin/manager only (writes server-gated).
 */
const CATEGORIES = ['client', 'referral_source', 'staff'] as const;
const CHANNELS = ['call', 'email', 'sms', 'mail', 'task'] as const;
const catLabel = (c: string) =>
  c === 'referral_source' ? 'Referral Source' : c === 'staff' ? 'Staff' : 'Client';
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type Cadence = { position?: number; offsetDays: number; channels: string[]; substatus?: string };
type Rule = { id: string; name: string; category: string; cadences?: Cadence[] };
type Draft = { name: string; category: string; cadences: Cadence[] };
const EMPTY: Draft = { name: '', category: 'client', cadences: [] };

export function TouchRulesModal({ onClose }: { onClose: () => void }) {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get<Rule[]>('/touch-rules')
      .then((r) => {
        setRules(Array.isArray(r) ? r : []);
        setErr(null);
      })
      .catch(() => setErr('Could not load touch rules.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const resetForm = () => {
    setEditId(null);
    setDraft(EMPTY);
  };
  const startEdit = (r: Rule) => {
    setEditId(r.id);
    setDraft({
      name: r.name,
      category: r.category,
      cadences: (r.cadences ?? []).map((c) => ({
        offsetDays: c.offsetDays,
        channels: c.channels ?? [],
      })),
    });
  };

  const addStep = () =>
    setDraft((d) => ({ ...d, cadences: [...d.cadences, { offsetDays: 0, channels: ['call'] }] }));
  const removeStep = (i: number) =>
    setDraft((d) => ({ ...d, cadences: d.cadences.filter((_, x) => x !== i) }));
  const setStepDays = (i: number, days: number) =>
    setDraft((d) => ({
      ...d,
      cadences: d.cadences.map((c, x) => (x === i ? { ...c, offsetDays: days } : c)),
    }));
  const toggleStepChannel = (i: number, ch: string) =>
    setDraft((d) => ({
      ...d,
      cadences: d.cadences.map((c, x) =>
        x === i
          ? {
              ...c,
              channels: c.channels.includes(ch)
                ? c.channels.filter((k) => k !== ch)
                : [...c.channels, ch],
            }
          : c,
      ),
    }));

  const save = () => {
    const name = draft.name.trim();
    if (!name || busy) return;
    // Every step needs at least one channel (server requires channels.min(1)).
    if (draft.cadences.some((c) => c.channels.length === 0)) {
      setErr('Each step needs at least one channel.');
      return;
    }
    setBusy(true);
    const body = {
      name,
      category: draft.category,
      cadences: draft.cadences.map((c, i) => ({
        position: i,
        offsetDays: c.offsetDays,
        channels: c.channels,
      })),
    };
    const req = editId ? api.patch(`/touch-rules/${editId}`, body) : api.post('/touch-rules', body);
    req
      .then(() => {
        resetForm();
        load();
      })
      .catch(() => setErr('Could not save the rule.'))
      .finally(() => setBusy(false));
  };

  const remove = async (r: Rule) => {
    if (!(await askConfirm(`Delete touch rule “${r.name}”?`))) return;
    api
      .del(`/touch-rules/${r.id}`)
      .then(() => {
        if (editId === r.id) resetForm();
        load();
      })
      .catch(() => setErr('Could not delete.'));
  };

  return (
    <Modal
      title="Touch Rules"
      onClose={onClose}
      className="actions-modal"
      actions={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="muted config-sub">
        Named cadence templates — each step fires on its day offset across the chosen channels.
        Contacts are enrolled from an anchor date.
      </p>
      {err && <div className="config-err">{err}</div>}

      <div className="action-form">
        <div className="action-form-row">
          <label className="action-field action-field-grow">
            <span>Rule name</span>
            <input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Intake Outreach"
            />
          </label>
          <label className="action-field">
            <span>Category</span>
            <Dd
              value={draft.category}
              onChange={(v) => setDraft((d) => ({ ...d, category: v }))}
              ariaLabel="Category"
              options={CATEGORIES.map((c) => ({ value: c, label: catLabel(c) }))}
            />
          </label>
        </div>

        <div className="tr-steps">
          <div className="tr-steps-head">
            <span>Cadence steps</span>
            <button type="button" className="action-row-btn" onClick={addStep}>
              + Add step
            </button>
          </div>
          {draft.cadences.length === 0 ? (
            <p className="muted" style={{ margin: '4px 0' }}>
              No steps yet — add one, or save a rule with none.
            </p>
          ) : (
            draft.cadences.map((c, i) => (
              <div className="tr-step" key={i}>
                <span className="tr-step-n">{i + 1}</span>
                <label className="tr-step-days">
                  Day
                  <input
                    type="number"
                    min={0}
                    value={c.offsetDays}
                    onChange={(e) => setStepDays(i, Number(e.target.value) || 0)}
                  />
                </label>
                <div className="tr-step-channels">
                  {CHANNELS.map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      className={`tr-chan ${c.channels.includes(ch) ? 'on' : ''}`}
                      onClick={() => toggleStepChannel(i, ch)}
                    >
                      {cap(ch)}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="config-del"
                  aria-label={`Remove step ${i + 1}`}
                  onClick={() => removeStep(i)}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>

        <div className="action-form-actions">
          {editId && (
            <button type="button" className="btn" onClick={resetForm} disabled={busy}>
              Cancel edit
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            onClick={save}
            disabled={busy || !draft.name.trim()}
          >
            {editId ? 'Save changes' : 'Add rule'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rules.length === 0 ? (
        <p className="muted config-empty">No touch rules yet.</p>
      ) : (
        <div className="action-list">
          {rules.map((r) => (
            <div className={`action-row ${editId === r.id ? 'editing' : ''}`} key={r.id}>
              <div className="action-row-main">
                <span className="action-row-label">{r.name}</span>
                <span className="action-row-meta">
                  {catLabel(r.category)} · {(r.cadences ?? []).length} step
                  {(r.cadences ?? []).length === 1 ? '' : 's'}
                </span>
              </div>
              <button type="button" className="action-row-btn" onClick={() => startEdit(r)}>
                Edit
              </button>
              <button
                type="button"
                className="config-del"
                onClick={() => remove(r)}
                aria-label={`Delete ${r.name}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
